import React, { useEffect, useState } from "react";
import api from "../api";
import Dialog from "../ui/Dialog";
import InlineAlert from "../ui/InlineAlert";
import StartDecisionReview from "../reviews/StartDecisionReview";
import RepositoryConnectPanel from "./RepositoryConnectPanel";
import { useReviewLaunch } from "../app/reviewLaunch";
import WorkspaceOverviewSkeleton from "./WorkspaceOverviewSkeleton";
import WorkspaceStatusPanel from "./WorkspaceStatusPanel";
import WorkspacePipeline from "./WorkspacePipeline";
import WorkspaceAttentionCard from "./WorkspaceAttentionCard";
import ReviewBalanceCard from "./ReviewBalanceCard";
import WorkspaceCodebaseList from "./WorkspaceCodebaseList";
import { guardrailCopy, smallestPackage } from "./workspaceOverviewModel";
import "./workspaceOverview.css";

export default function WorkspaceOverview({
  mutateAllowed = true,
  canConnect = false,
  repositories = [],
  setup = null,
  onRepositoryAdded,
}) {
  const launch = useReviewLaunch();
  const [overview, setOverview] = useState(null);
  const [packages, setPackages] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [connectOpen, setConnectOpen] = useState(false);
  const [localReview, setLocalReview] = useState(false);

  useEffect(() => {
    const billing = new URLSearchParams(window.location.search).get("billing");
    if (billing === "success") setNotice("Payment received. Review credits appear after Stripe confirms the payment.");
    if (billing === "cancelled") setNotice("Checkout was cancelled. No charge was made.");
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get("/workspace/overview").then((response) => {
      if (!cancelled) setOverview(response.data || {});
    }).catch(() => {
      if (!cancelled) setError("The workspace could not be loaded.");
    });
    const packagesRequest = api.get("/billing/review-credit-packages");
    if (packagesRequest && typeof packagesRequest.then === "function") {
      packagesRequest.then((response) => {
        if (cancelled) return;
        const rows = response.data?.packages || [];
        const smallest = smallestPackage(rows);
        const ordered = smallest ? [smallest, ...rows.filter((item) => item.id !== smallest.id)] : rows;
        setPackages(ordered);
      }).catch(() => {
        if (!cancelled) setPackages([]);
      });
    }
    return () => {
      cancelled = true;
    };
  }, []);

  function openReview() {
    if (launch?.open) launch.open();
    else setLocalReview(true);
  }

  if (!overview && !error) return <WorkspaceOverviewSkeleton />;
  if (!overview) {
    return (
      <div className="rr-overview">
        <header className="rr-overview-head">
          <p className="rr-eyebrow">Workspace</p>
          <h1>Review Control Center</h1>
        </header>
        <InlineAlert tone="alert">{error}</InlineAlert>
      </div>
    );
  }

  const copy = guardrailCopy(overview);

  return (
    <div className="rr-overview">
      <header className="rr-overview-head">
        <p className="rr-eyebrow">Workspace</p>
        <h1>Review Control Center</h1>
        <p>RenderRepo understands your product and checks every change against it.</p>
      </header>
      {error ? <InlineAlert tone="alert">{error}</InlineAlert> : null}
      <WorkspaceStatusPanel
        overview={overview}
        readOnly={!mutateAllowed || overview.setup?.readOnly}
        canConnect={canConnect && !overview.setup?.readOnly}
        onReview={openReview}
        onAddCodebase={() => setConnectOpen(true)}
      />
      {copy.showPipeline ? <WorkspacePipeline overview={overview} /> : null}
      <div className="rr-overview-split">
        <WorkspaceAttentionCard overview={overview} />
        <ReviewBalanceCard
          credits={overview.credits}
          packages={packages}
          canPurchase={Boolean(overview.credits?.canPurchase) && !overview.setup?.readOnly}
          notice={notice}
        />
      </div>
      <WorkspaceCodebaseList
        codebases={overview.codebases || []}
        canConnect={canConnect && !overview.setup?.readOnly}
        onAddCodebase={() => setConnectOpen(true)}
      />
      {connectOpen ? (
        <Dialog title="Add a codebase" onClose={() => setConnectOpen(false)}>
          <RepositoryConnectPanel
            setup={setup || { installationState: overview.setup?.installationState }}
            repositories={repositories}
            canConnect={canConnect}
            initiallyOpen
            onConnected={(repository) => {
              onRepositoryAdded?.(repository);
              setConnectOpen(false);
            }}
          />
        </Dialog>
      ) : null}
      {localReview ? (
        <Dialog title="Review a pull request" onClose={() => setLocalReview(false)}>
          <StartDecisionReview embedded quiet mutateAllowed={mutateAllowed} onStarted={() => setLocalReview(false)} />
        </Dialog>
      ) : null}
    </div>
  );
}
